import * as THREE from 'three';
import store from '../../redux/store';
import {
  selected_object,
  setPushIntersectedObject,
} from '../../redux/Slice/gridSlice';
import { selectObjectInScene } from '../../pages/Dashboard/GridPage';
import {
  startAnimation,
  stopAnimation,
  pauseAnimation,
} from './animationControls';
import { setLifeTimeout, clearLifeTimeout } from './lifeUtils';

const CLOUD_SPREAD_MAX = 180;

// Global animation manager to prevent multiple loops
const AnimationManager = {
  animationId: null,
  cloudSystems: new Set(),
  isRunning: false,
  lastFrameTime: 0,
  targetFPS: 60,
  frameInterval: 1000 / 60, // ~16.67ms per frame

  register(cloudSystem) {
    this.cloudSystems.add(cloudSystem);
    if (!this.isRunning) {
      this.start();
    }
  },

  unregister(cloudSystem) {
    this.cloudSystems.delete(cloudSystem);
    if (this.cloudSystems.size === 0) {
      this.stop();
    }
  },

  start() {
    if (this.isRunning) return;
    this.isRunning = true;
    this.lastFrameTime = performance.now();
    this.animate();
  },

  stop() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    this.isRunning = false;
  },

  animate() {
    if (!this.isRunning) return;

    const currentTime = performance.now();
    const deltaTime = currentTime - this.lastFrameTime;

    // Frame throttling - only animate if enough time has passed
    if (deltaTime >= this.frameInterval) {
      this.lastFrameTime = currentTime - (deltaTime % this.frameInterval);
      const time = currentTime * 0.0005;

      // Animate all registered cloud systems
      this.cloudSystems.forEach((cloudGroup) => {
        // Check if still in scene
        if (!cloudGroup.parent) {
          this.unregister(cloudGroup);
          return;
        }

        // Skip if animation is paused or stopped
        if (cloudGroup.userData?.isPaused || cloudGroup.userData?.isStopped) {
          return;
        }

        const spread = cloudGroup.userData?.spread || CLOUD_SPREAD_MAX;
        const halfRange = Math.max(1, spread * 0.5);

        // Animate clusters - filter to only process actual cluster groups
        // Other objects added via hierarchy won't have the expected cluster structure
        const clusters = cloudGroup.children.filter((child) => {
          // Only process groups that have cluster-specific userData
          return (
            child.isGroup &&
            child.userData &&
            typeof child.userData.driftDirection === 'number' &&
            typeof child.userData.originalY === 'number'
          );
        });

        for (let i = 0; i < clusters.length; i++) {
          const cluster = clusters[i];
          const clusterData = cluster.userData;

          // Safety check - ensure clusterData exists
          if (!clusterData || typeof clusterData.driftDirection !== 'number') {
            continue;
          }

          // Horizontal drift
          cluster.position.x +=
            Math.cos(clusterData.driftDirection) *
            (clusterData.driftSpeed || 0.008);
          cluster.position.z +=
            Math.sin(clusterData.driftDirection) *
            (clusterData.driftSpeed || 0.008);

          // Vertical floating - ensure originalY exists
          if (typeof clusterData.originalY === 'number') {
            cluster.position.y =
              clusterData.originalY +
              Math.sin(time * 0.8 + i) * (clusterData.verticalRange || 2);
          }

          // Boundary wrapping (same style as rain/snow: preserve overflow amount)
          cluster.position.x = wrapAxisInBounds(cluster.position.x, halfRange);
          cluster.position.z = wrapAxisInBounds(cluster.position.z, halfRange);

          // Animate particles in cluster - filter to only process actual particle meshes
          const particles = cluster.children.filter((child) => {
            // Only process meshes that have particle-specific userData
            return (
              child.isMesh &&
              child.userData &&
              child.userData.originalPosition &&
              typeof child.userData.originalPosition.y === 'number'
            );
          });

          for (let j = 0; j < particles.length; j++) {
            const particleMesh = particles[j];
            const particleData = particleMesh.userData;

            // Safety check - ensure particleData and originalPosition exist
            if (!particleData || !particleData.originalPosition) {
              continue;
            }

            // Floating motion
            const floatOffset =
              Math.sin(
                time * (particleData.floatSpeed || 0.5) +
                (particleData.animationOffset || 0)
              ) * 0.3;

            if (typeof particleData.originalPosition.y === 'number') {
              particleMesh.position.y =
                particleData.originalPosition.y + floatOffset;
            }

            // Rotation
            if (typeof particleData.rotationSpeed === 'number') {
              particleMesh.rotation.y += particleData.rotationSpeed;
              particleMesh.rotation.z += particleData.rotationSpeed * 0.7;
            }

            // Scale breathing
            const breathe =
              Math.sin(time * 0.6 + (particleData.animationOffset || 0)) *
              0.05 +
              1;
            particleMesh.scale.setScalar(breathe);
          }
        }
      });
    }

    this.animationId = requestAnimationFrame(() => this.animate());
  },
};

/** Cluster count from UI (`clusterCount`) or legacy `cloudCount`; not scaled by spread `range`. */
function resolveCloudClusterCount(particle) {
  const raw =
    particle?.clusterCount ??
    particle?.cloudCount ??
    particle?.userData?.clusterCount ??
    particle?.userData?.baseCloudCount ??
    15;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 1) return 15;
  return Math.min(Math.round(n), 200);
}

const wrapAxisInBounds = (value, halfRange) => {
  if (halfRange <= 0) return 0;
  if (value > halfRange) return -halfRange + (value - halfRange);
  if (value < -halfRange) return halfRange + (value + halfRange);
  return value;
};

// Helper to compute a cluster's X/Z position based on spread bounds.
// Normalized coords are stored so range updates can restore the original layout.
const computeClusterPosition = (spread = CLOUD_SPREAD_MAX) => {
  const halfSpread = Math.max(1, spread * 0.5);
  const normalizedX = (Math.random() - 0.5) * 2;
  const normalizedZ = (Math.random() - 0.5) * 2;
  return {
    x: normalizedX * halfSpread,
    z: normalizedZ * halfSpread,
    normalizedX,
    normalizedZ,
  };
};

const handleClouds = (scene, particle) => {
  if (!scene) return;


  const baseCloudCount = resolveCloudClusterCount(particle);
  const cloudCount = baseCloudCount;

  const particleCount = particle?.particleCount || 2;
  const clusterSize = particle?.clusterSize || 8;
  const altitude = particle?.altitude || { min: 20, max: 28 };
  // Range multiplier controls how far clusters are placed from the origin (multiplies `spread`)
  const range = particle?.range ?? 20;
  // Scale spread based on range: 0-100 range → 0-CLOUD_SPREAD_MAX spread
  const spread = Math.round((range / 100) * CLOUD_SPREAD_MAX);
  const opacity = particle?.opacity ?? 0.9;
  const color = particle?.color || '#ffffff';
  const driftSpeed = particle?.driftSpeed || 0.008;
  const floatSpeed = particle?.floatSpeed || 0.5;
  const verticalRange = particle?.verticalRange || 2;

  const cloudGroup = new THREE.Group();
  const life = particle?.life || 0;

  // Dynamic cloud material with soft shadow support
  const createCloudMaterial = (particleData) => {
    const cloudColor = particleData?.color
      ? new THREE.Color(particleData.color)
      : new THREE.Color(color);
    return new THREE.MeshStandardMaterial({
      color: cloudColor,
      transparent: true,
      opacity:
        particleData?.opacity !== undefined ? particleData.opacity : opacity,
      side: THREE.DoubleSide,
      roughness: 0.8, // Add roughness for realistic lighting
      metalness: 0.0, // Non-metallic clouds
      // shadowSide: THREE.DoubleSide, // Enable shadows on both sides
      // alphaTest: 0.1, // Enable alpha test for softer shadow edges
      shadowMap: { type: THREE.PCFSoftShadowMap }, // Soft shadows
    });
  };

  // Create realistic cloud formations
  for (let cluster = 0; cluster < cloudCount; cluster++) {
    const clusterGroup = new THREE.Group();

    // Dynamic particle count per cluster
    const clusterParticleCount =
      Math.floor(Math.random() * (particleCount * 0.5)) + particleCount * 0.75;
    const dynamicClusterSize = clusterSize + Math.random() * 6;

    // Position clouds at dynamic altitudes and distances
    const {
      x: clusterX,
      z: clusterZ,
      normalizedX,
      normalizedZ,
    } = computeClusterPosition(spread);
    const clusterY =
      Math.random() * (altitude.max - altitude.min) + altitude.min;

    for (let i = 0; i < clusterParticleCount; i++) {
      // Dynamic cloud particle sizes
      const radius = Math.random() * 1.5 + 0.8;
      const geometry = new THREE.SphereGeometry(radius, 8, 6);
      const material = createCloudMaterial(particle);

      // Vary opacity for depth
      material.opacity = Math.random() * 0.3 + 0.7;

      const particleMesh = new THREE.Mesh(geometry, material);

      // More natural clustering - denser in middle
      const distance = Math.pow(Math.random(), 2) * dynamicClusterSize;
      const angle = Math.random() * Math.PI * 2;
      const verticalOffset = (Math.random() - 0.5) * 1;

      particleMesh.position.set(
        Math.cos(angle) * distance,
        verticalOffset,
        Math.sin(angle) * distance
      );

      // Natural scale variation
      const scale = Math.random() * 0.6 + 0.7;
      particleMesh.scale.setScalar(scale);

      // Enable shadow casting and receiving
      particleMesh.castShadow = true;
      particleMesh.receiveShadow = true;

      // Store animation data and base geometry info so we can resize later
      particleMesh.userData = {
        originalPosition: particleMesh.position.clone(),
        animationOffset: Math.random() * Math.PI * 2,
        floatSpeed: Math.random() * floatSpeed + 0.3,
        rotationSpeed: (Math.random() - 0.5) * 0.01,
        originalRadius: radius,
        baseMeshScale: scale,
      };

      clusterGroup.add(particleMesh);
    }

    clusterGroup.position.set(clusterX, clusterY, clusterZ || clusterZ);

    // Store cluster animation data
    clusterGroup.userData = {
      driftSpeed: Math.random() * driftSpeed + 0.005,
      driftDirection: Math.random() * Math.PI * 2,
      originalY: clusterY,
      verticalRange: Math.random() * verticalRange + 1,
      normalizedX,
      normalizedZ,
      range: range,
      spread: spread,
    };

    cloudGroup.add(clusterGroup);
  }

  cloudGroup.name = 'clouds';
  cloudGroup.deleteType = 'particle';
  cloudGroup.objType = 'clouds';
  const uniqueId = `clouds_${Date.now()}`;

  // Set the name based on particle data or generate unique ID
  if (particle && particle.id) {
    cloudGroup.name = particle.id;
  } else if (particle && particle._id) {
    cloudGroup.name = particle._id;
  } else {
    cloudGroup.name = uniqueId;
  }

  cloudGroup.displayName = 'Clouds'; // For UI display
  cloudGroup.url = uniqueId;
  cloudGroup.asset_file_name = 'clouds';
  cloudGroup.file_name = 'clouds';
  cloudGroup.mesh_code = 'clouds';
  cloudGroup.glb_type = 'shapes';
  cloudGroup.createdAt = new Date().toISOString();
  cloudGroup.userData = {
    material_settings: {
      shader: 'Standard',
      albedo: color,
      roughness: 0.8,
      metalness: 0.0,
      castShadow: true,
      receiveShadow: true,
    },
    // Store dynamic properties for animation
    baseCloudCount,
    clusterCount: baseCloudCount,
    cloudCount,
    particleCount: particleCount,
    clusterSize: clusterSize,
    altitude: altitude,
    spread: spread,
    range: range,
    opacity: opacity,
    color: color,
    driftSpeed: driftSpeed,
    floatSpeed: floatSpeed,
    verticalRange: verticalRange,
    isPaused: false,
    isStopped: particle?.isStopped || false,
    life,
  };

  // Set transform from particle data
  if (particle?.position) {
    cloudGroup.position.set(
      particle.position.x || 0,
      particle.position.y || 0,
      particle.position.z || 0
    );
  }
  if (particle?.rotation) {
    cloudGroup.rotation.set(
      particle.rotation.x || 0,
      particle.rotation.y || 0,
      particle.rotation.z || 0
    );
  }
  if (particle?.scale) {
    cloudGroup.scale.set(
      particle.scale.x || 1,
      particle.scale.y || 1,
      particle.scale.z || 1
    );
  }
  cloudGroup.file_name = cloudGroup.name;
  cloudGroup.temp_file_name = cloudGroup.temp_file_name || cloudGroup.name;

  scene.add(cloudGroup);
  store.dispatch(setPushIntersectedObject(cloudGroup));

  // Register with animation manager if not stopped
  if (!particle?.isStopped) {
    AnimationManager.register(cloudGroup);
    setLifeTimeout(
      cloudGroup,
      typeof life === 'number' ? life : parseFloat(life) || 0
    );
  }

  store.dispatch(selected_object(cloudGroup));
  selectObjectInScene(cloudGroup.name);

  return cloudGroup;
};

const isSameNumber = (a, b) => Number(a) === Number(b);

/** Lightweight live color preview — no Redux / history / selection churn. */
export function previewCloudColor(cloudSystem, colorHex) {
  if (!cloudSystem || cloudSystem.objType !== 'clouds' || !colorHex) return;

  const newColor = new THREE.Color(colorHex);
  cloudSystem.traverse((child) => {
    if (
      child.isMesh &&
      child.userData?.originalPosition &&
      child.material?.color
    ) {
      child.material.color.copy(newColor);
    }
  });

  if (cloudSystem.userData) {
    cloudSystem.userData.color = colorHex;
    if (cloudSystem.userData.material_settings) {
      cloudSystem.userData.material_settings.albedo = colorHex;
    }
  }
}

const isSameScaleValue = (nextScale, cloudSystem) => {
  if (nextScale == null) return true;
  if (typeof nextScale === 'number') {
    const s = Number(cloudSystem?.scale?.x) || 1;
    return isSameNumber(nextScale, s);
  }
  const nx = Number(nextScale.x);
  const ny = Number(nextScale.y);
  const nz = Number(nextScale.z);
  return (
    isSameNumber(nx, cloudSystem?.scale?.x) &&
    isSameNumber(ny, cloudSystem?.scale?.y) &&
    isSameNumber(nz, cloudSystem?.scale?.z)
  );
};

const updateClouds = (scene, cloudSystem, newParticleData) => {
  if (!scene || !cloudSystem || cloudSystem.objType !== 'clouds') {
    console.warn('Invalid cloud system or scene provided for update');
    return;
  }

  // Get current configuration
  const currentConfig = cloudSystem.userData || {};

  // Handle animation state changes
  if (
    typeof newParticleData?.isPlaying !== 'undefined' ||
    typeof newParticleData?.isStopped !== 'undefined'
  ) {
    const isPlaying =
      newParticleData?.isPlaying ?? currentConfig.isPlaying ?? true;
    const isStopped =
      newParticleData?.isStopped ?? currentConfig.isStopped ?? false;

    if (isStopped) {
      cloudSystem.userData.isStopped = true;
      cloudSystem.userData.isPaused = false;
      AnimationManager.unregister(cloudSystem);
      stopAnimation(cloudSystem, cloudSystem.name);
    } else if (isPlaying) {
      cloudSystem.userData.isStopped = false;
      cloudSystem.userData.isPaused = false;
      AnimationManager.register(cloudSystem);
      startAnimation(cloudSystem, () => { }, cloudSystem.name);
    } else {
      cloudSystem.userData.isPaused = true;
      cloudSystem.userData.isStopped = false;
      pauseAnimation(cloudSystem, cloudSystem.name);
    }
  }

  // Merge current config with new particle data
  const baseCloudCount = resolveCloudClusterCount({
    ...currentConfig,
    ...newParticleData,
  });
  const rangeValue = newParticleData?.range ?? currentConfig.range ?? 20;
  // Scale spread based on range: 0-100 range → 0-CLOUD_SPREAD_MAX spread
  const spreadValue = Math.round((rangeValue / 100) * CLOUD_SPREAD_MAX);

  const prevParticleCount =
    currentConfig.particleCount ?? cloudSystem.userData?.particleCount ?? 20;
  const nextParticleCount =
    newParticleData?.particleCount ?? prevParticleCount;

  const updatedConfig = {
    baseCloudCount,
    clusterCount: baseCloudCount,
    cloudCount: baseCloudCount,
    particleCount: nextParticleCount,
    clusterSize: newParticleData?.clusterSize || currentConfig.clusterSize || 8,
    altitude: newParticleData?.altitude ||
      currentConfig.altitude || { min: 20, max: 28 },
    spread: spreadValue,
    range: rangeValue,
    opacity: newParticleData?.opacity ?? currentConfig.opacity ?? 0.9,
    color: newParticleData?.color || currentConfig.color || '#ffffff',
    size: newParticleData?.size ?? currentConfig.size,
    radius: newParticleData?.radius ?? currentConfig.radius,
    driftSpeed:
      newParticleData?.driftSpeed || currentConfig.driftSpeed || 0.008,
    floatSpeed: newParticleData?.floatSpeed || currentConfig.floatSpeed || 0.5,
    verticalRange:
      newParticleData?.verticalRange || currentConfig.verticalRange || 2,
  };

  const colorChanged =
    typeof newParticleData?.color !== 'undefined' &&
    String(newParticleData.color).toLowerCase() !==
      String(currentConfig.color || '').toLowerCase();
  const opacityChanged =
    typeof newParticleData?.opacity !== 'undefined' &&
    !isSameNumber(newParticleData.opacity, currentConfig.opacity);
  const particleCountChanged = !isSameNumber(
    prevParticleCount,
    updatedConfig.particleCount
  );
  const rangeChanged =
    !isSameNumber(currentConfig.range ?? 20, updatedConfig.range) ||
    !isSameNumber(
      currentConfig.spread ??
        Math.round(((currentConfig.range ?? 20) / 100) * CLOUD_SPREAD_MAX),
      updatedConfig.spread
    );
  const scaleChanged =
    typeof newParticleData?.scale !== 'undefined' &&
    !isSameScaleValue(newParticleData.scale, cloudSystem);
  const sizeChanged =
    (typeof newParticleData?.size !== 'undefined' &&
      typeof currentConfig.size !== 'undefined' &&
      !isSameNumber(newParticleData.size, currentConfig.size)) ||
    (typeof newParticleData?.radius !== 'undefined' &&
      typeof currentConfig.radius !== 'undefined' &&
      !isSameNumber(newParticleData.radius, currentConfig.radius));
  const lifeChanged =
    typeof newParticleData?.life !== 'undefined' &&
    typeof currentConfig.life !== 'undefined' &&
    !isSameNumber(newParticleData.life, currentConfig.life);

  // Color/opacity edits must not rebuild/spread layout — ignore count desyncs here.
  const appearanceOnly =
    (colorChanged || opacityChanged) &&
    !rangeChanged &&
    !scaleChanged &&
    !sizeChanged &&
    !lifeChanged;
  const shouldResizeParticles = particleCountChanged && !appearanceOnly;

  // Update material properties for all cloud particles
  // Only update actual cloud particle meshes, not other objects added via hierarchy
  if (colorChanged || opacityChanged) {
    const newColor = new THREE.Color(updatedConfig.color);
    cloudSystem.traverse((child) => {
      // Only update meshes that are part of the cloud system (have originalPosition in userData)
      // This prevents updating materials of other objects added as children
      if (
        child.isMesh &&
        child.userData &&
        child.userData.originalPosition &&
        child.material &&
        child.material.color
      ) {
        if (colorChanged) {
          child.material.color.copy(newColor);
        }
        if (opacityChanged) {
          child.material.opacity = updatedConfig.opacity;
          child.material.transparent = updatedConfig.opacity < 1;
        }
        child.material.needsUpdate = true;
        // Maintain shadow properties during updates
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });
  }

  // If particleCount changed, add or remove particles per cluster to match new total
  try {
    const newParticleCount = updatedConfig.particleCount;

    if (shouldResizeParticles) {
      const dynamicClusterSize =
        updatedConfig.clusterSize || currentConfig.clusterSize || 8;
      // Absolute per-cluster target — do not scale from mismatched create/redux counts.
      const desiredCount = Math.max(1, Math.round(newParticleCount));

      // Filter to only process actual cluster groups (not other objects added via hierarchy)
      const clusterGroups = cloudSystem.children.filter((child) => {
        return (
          child.isGroup &&
          child.userData &&
          typeof child.userData.driftDirection === 'number'
        );
      });

      clusterGroups.forEach((clusterGroup) => {
        // Only operate on groups (clusters)
        if (!clusterGroup || !clusterGroup.userData) return;

        // Filter to only count actual particle meshes (not other objects)
        const particleMeshes = clusterGroup.children.filter((child) => {
          return (
            child.isMesh && child.userData && child.userData.originalPosition
          );
        });

        const currentCount = particleMeshes.length;

        if (desiredCount > currentCount) {
          // Add missing particles by cloning an existing particle if possible
          // Find first actual particle mesh as template
          const template =
            particleMeshes[0] ||
            clusterGroup.children.find((child) => child.isMesh);
          for (let k = 0; k < desiredCount - currentCount; k++) {
            let newMesh;
            if (template) {
              newMesh = template.clone();
              // Ensure geometry and material are separate copies
              if (
                newMesh.geometry &&
                typeof newMesh.geometry.clone === 'function'
              )
                newMesh.geometry = newMesh.geometry.clone();
              if (
                newMesh.material &&
                typeof newMesh.material.clone === 'function'
              )
                newMesh.material = newMesh.material.clone();
            } else {
              const radius = Math.random() * 1.5 + 0.8;
              newMesh = new THREE.Mesh(
                new THREE.SphereGeometry(radius, 8, 6),
                new THREE.MeshStandardMaterial({
                  color: new THREE.Color(updatedConfig.color),
                  transparent: true,
                  opacity: updatedConfig.opacity,
                  side: THREE.DoubleSide,
                  roughness: 0.8,
                  metalness: 0.0,
                })
              );
            }

            const distance = Math.pow(Math.random(), 2) * dynamicClusterSize;
            const angle = Math.random() * Math.PI * 2;
            const verticalOffset = (Math.random() - 0.5) * 2;

            newMesh.position.set(
              Math.cos(angle) * distance,
              verticalOffset,
              Math.sin(angle) * distance
            );
            const scale = Math.random() * 0.6 + 0.7;
            newMesh.scale.setScalar(scale);
            newMesh.castShadow = true;
            newMesh.receiveShadow = true;

            // Determine a reasonable original radius for this new mesh
            let createdRadius = 1;
            if (newMesh.geometry && newMesh.geometry.parameters && typeof newMesh.geometry.parameters.radius === 'number') {
              createdRadius = newMesh.geometry.parameters.radius;
            }

            newMesh.userData = {
              originalPosition: newMesh.position.clone(),
              animationOffset: Math.random() * Math.PI * 2,
              floatSpeed:
                Math.random() *
                (updatedConfig.floatSpeed ||
                  currentConfig.floatSpeed ||
                  0.5) +
                0.3,
              rotationSpeed: (Math.random() - 0.5) * 0.01,
              originalRadius: createdRadius,
              baseMeshScale: scale,
            };

            clusterGroup.add(newMesh);
          }
        } else if (desiredCount < currentCount) {
          // Remove extra particles from end of particle meshes array
          const removeCount = currentCount - desiredCount;
          // Get only particle meshes to remove
          const meshesToRemove = particleMeshes.slice(-removeCount);
          for (let r = 0; r < meshesToRemove.length; r++) {
            const child = meshesToRemove[r];
            if (!child) continue;
            clusterGroup.remove(child);
            if (child.geometry) child.geometry.dispose();
            if (child.material) {
              if (Array.isArray(child.material))
                child.material.forEach((m) => m.dispose());
              else child.material.dispose();
            }
          }
        }
      });
    }
  } catch (err) {
    // Non-fatal: log but allow execution to continue
    // eslint-disable-next-line no-console
    console.warn('Error while resizing cloud particles:', err);
  }

  // If range/spread changed, restore layout from stored normalized coords (not current drifted positions)
  try {
    if (rangeChanged) {
      const prevRange = currentConfig.range ?? cloudSystem.userData?.range ?? 20;
      const prevSpread =
        currentConfig.spread ??
        cloudSystem.userData?.spread ??
        Math.round((prevRange / 100) * CLOUD_SPREAD_MAX);

      // Filter to only process actual cluster groups (not other objects added via hierarchy)
      const clusterGroups = cloudSystem.children.filter((child) => {
        return (
          child.isGroup &&
          child.userData &&
          typeof child.userData.driftDirection === 'number'
        );
      });

      const halfSpread = Math.max(0, updatedConfig.spread * 0.5);
      const prevHalfSpread = Math.max(1, prevSpread * 0.5);

      clusterGroups.forEach((clusterGroup) => {
        if (!clusterGroup) return;
        const clusterY = clusterGroup.position.y;
        if (!clusterGroup.userData) clusterGroup.userData = {};

        let { normalizedX, normalizedZ } = clusterGroup.userData;

        // Legacy clouds created before normalized coords were stored
        if (
          typeof normalizedX !== 'number' ||
          typeof normalizedZ !== 'number'
        ) {
          normalizedX = clusterGroup.position.x / prevHalfSpread;
          normalizedZ = clusterGroup.position.z / prevHalfSpread;
          clusterGroup.userData.normalizedX = normalizedX;
          clusterGroup.userData.normalizedZ = normalizedZ;
        }

        clusterGroup.position.set(
          normalizedX * halfSpread,
          clusterY,
          normalizedZ * halfSpread
        );
        clusterGroup.userData.range = updatedConfig.range;
        clusterGroup.userData.spread = updatedConfig.spread;
      });

      // Refresh world matrices/bounds immediately so transform box updates without popping.
      cloudSystem.updateMatrixWorld(true);
      if (cloudSystem.userData) cloudSystem.userData.forceBoxUpdate = true;
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(
      'Error while repositioning clusters for new range/spread:',
      err
    );
  }

  // Update transforms only when values actually differ from the scene object
  if (newParticleData?.position) {
    const p = newParticleData.position;
    const nextX = p.x !== undefined ? p.x : cloudSystem.position.x;
    const nextY = p.y !== undefined ? p.y : cloudSystem.position.y;
    const nextZ = p.z !== undefined ? p.z : cloudSystem.position.z;
    if (
      !isSameNumber(nextX, cloudSystem.position.x) ||
      !isSameNumber(nextY, cloudSystem.position.y) ||
      !isSameNumber(nextZ, cloudSystem.position.z)
    ) {
      cloudSystem.position.set(nextX, nextY, nextZ);
    }
  }
  if (newParticleData?.rotation) {
    const r = newParticleData.rotation;
    const nextX = r.x !== undefined ? r.x : cloudSystem.rotation.x;
    const nextY = r.y !== undefined ? r.y : cloudSystem.rotation.y;
    const nextZ = r.z !== undefined ? r.z : cloudSystem.rotation.z;
    if (
      !isSameNumber(nextX, cloudSystem.rotation.x) ||
      !isSameNumber(nextY, cloudSystem.rotation.y) ||
      !isSameNumber(nextZ, cloudSystem.rotation.z)
    ) {
      cloudSystem.rotation.set(nextX, nextY, nextZ);
    }
  }
  if (scaleChanged && newParticleData?.scale) {
    cloudSystem.scale.set(
      newParticleData.scale.x !== undefined
        ? newParticleData.scale.x
        : cloudSystem.scale.x,
      newParticleData.scale.y !== undefined
        ? newParticleData.scale.y
        : cloudSystem.scale.y,
      newParticleData.scale.z !== undefined
        ? newParticleData.scale.z
        : cloudSystem.scale.z
    );
  }

  // If a scale value actually changed, also update each particle's base geometry
  // so sphere radius changes (preserves per-mesh variation stored in userData)
  if (scaleChanged) {
    // compute uniform scalar from number or vector
    let scalar = 1;
    const s = newParticleData.scale;
    if (typeof s === 'number') {
      scalar = s;
    } else if (s && typeof s === 'object') {
      const vals = [s.x, s.y, s.z].filter((v) => typeof v === 'number');
      scalar = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 1;
    }

    // Only operate on actual cluster groups
    const clusterGroups = cloudSystem.children.filter((child) => {
      return (
        child.isGroup &&
        child.userData &&
        typeof child.userData.driftDirection === 'number'
      );
    });

    clusterGroups.forEach((clusterGroup) => {
      const particleMeshes = clusterGroup.children.filter((child) => {
        return (
          child.isMesh && child.userData && child.userData.originalPosition
        );
      });

      particleMeshes.forEach((mesh) => {
        try {
          const ud = mesh.userData || {};
          const origR = typeof ud.originalRadius === 'number' ? ud.originalRadius : 1;
          const baseScale = typeof ud.baseMeshScale === 'number' ? ud.baseMeshScale : 1;

          if (mesh.geometry) mesh.geometry.dispose();
          mesh.geometry = new THREE.SphereGeometry(origR * scalar, 8, 6);

          // Preserve per-mesh scale variation
          mesh.scale.setScalar(baseScale);

          if (mesh.userData) mesh.userData.originalPosition = mesh.position.clone();
        } catch (err) {
          // ignore non-fatal errors
        }
      });
    });
  }

  // Also support `size` or `radius` updates coming from UI controls.
  // `size` is treated as a uniform multiplier; `radius` sets an absolute radius.
  if (sizeChanged) {
    // Determine scalar: if `radius` provided, compute scalar per-mesh as radius/origR.
    // For uniform `size`, use it directly as multiplier.
    const isAbsoluteRadius = typeof newParticleData?.radius === 'number';
    const sizeVal = newParticleData?.size;
    const absRadius = newParticleData?.radius;

    const clusterGroups2 = cloudSystem.children.filter((child) => {
      return (
        child.isGroup &&
        child.userData &&
        typeof child.userData.driftDirection === 'number'
      );
    });

    clusterGroups2.forEach((clusterGroup) => {
      const particleMeshes = clusterGroup.children.filter((child) => {
        return (
          child.isMesh && child.userData && child.userData.originalPosition
        );
      });

      particleMeshes.forEach((mesh) => {
        try {
          const ud = mesh.userData || {};
          const origR = typeof ud.originalRadius === 'number' ? ud.originalRadius : 1;
          const baseScale = typeof ud.baseMeshScale === 'number' ? ud.baseMeshScale : 1;

          // compute new radius
          let newR = origR;
          if (isAbsoluteRadius) {
            // set absolute radius
            newR = absRadius;
          } else if (typeof sizeVal === 'number') {
            newR = origR * sizeVal;
          }

          if (mesh.geometry) mesh.geometry.dispose();
          mesh.geometry = new THREE.SphereGeometry(newR, 8, 6);
          mesh.scale.setScalar(baseScale);
          if (mesh.userData) mesh.userData.originalPosition = mesh.position.clone();
        } catch (err) {
          // ignore
        }
      });
    });
  }

  // Update userData with new configuration
  cloudSystem.userData = {
    ...cloudSystem.userData,
    ...updatedConfig,
    material_settings: {
      ...cloudSystem.userData.material_settings,
      albedo: updatedConfig.color,
      castShadow: true,
      receiveShadow: true,
    },
  };

  // Manage life timeout only when Life(sec) actually changes
  if (lifeChanged) {
    try {
      clearLifeTimeout(cloudSystem);
      const lifeVal = newParticleData?.life ?? cloudSystem.userData?.life;
      const lifeSeconds =
        typeof lifeVal === 'number' ? lifeVal : parseFloat(lifeVal);
      if (!isNaN(lifeSeconds) && lifeSeconds > 0) {
        setLifeTimeout(cloudSystem, lifeSeconds);
        cloudSystem.visible = true;
        if (cloudSystem.userData) {
          cloudSystem.userData.isStopped = false;
          cloudSystem.userData.isPaused = false;
          cloudSystem.userData.isPlaying = true;
        }
      }
    } catch (e) {
      // ignore
    }
  }

  // Update system metadata if ID changed
  if (newParticleData?.id && newParticleData.id !== cloudSystem.name) {
    cloudSystem.name = newParticleData.id;
    cloudSystem.url = newParticleData.id;
  }

  // Only dispatch selection refresh for structural updates (not color/opacity drag)
  if (!appearanceOnly) {
    store.dispatch(setPushIntersectedObject(cloudSystem));
    if (cloudSystem.userData) cloudSystem.userData.forceBoxUpdate = true;
  }

  return cloudSystem;
};

// Helper function to find cloud system by name/id in scene
const findCloudSystem = (scene, nameOrId) => {
  return scene.children.find(
    (child) =>
      (child.name === nameOrId || child.url === nameOrId) &&
      child.objType === 'clouds'
  );
};

// Convenience function to update clouds by name
const updateCloudsByName = (scene, nameOrId, newParticleData) => {
  const cloudSystem = findCloudSystem(scene, nameOrId);
  if (cloudSystem) {
    return updateClouds(scene, cloudSystem, newParticleData);
  } else {
    console.warn(`Cloud system with name/id "${nameOrId}" not found in scene`);
    return null;
  }
};

// Cleanup function to properly dispose of cloud system
const cleanupClouds = (cloudGroup) => {
  if (!cloudGroup) return;

  // Unregister from animation manager
  AnimationManager.unregister(cloudGroup);

  // Dispose geometries and materials
  cloudGroup.traverse((child) => {
    if (child.geometry) {
      child.geometry.dispose();
    }
    if (child.material) {
      if (Array.isArray(child.material)) {
        child.material.forEach((material) => material.dispose());
      } else {
        child.material.dispose();
      }
    }
  });
};

export default handleClouds;
export {
  updateClouds,
  updateCloudsByName,
  cleanupClouds,
  AnimationManager,
};
